/*
  Shared behaviour of the product sub-sections.

  window.FableAccordion: height animation for native <details> rows
  (.fable-accordion on the product page, .fable-faq in the FAQ section).
  One delegated click listener, so rows added by the theme editor work
  without re-initialising. Without JS, or with reduced motion, the rows are
  plain <details>.

  Feature grid: every looping tile video gets a pause / play button. Videos start
  paused under reduced motion or data saver; the visitor's own choice wins either way.

  Theme editor: a selected photo is scrolled into view, a selected question or
  accordion row is opened.
*/
(function () {
  if (!window.FableAccordion) {
    const SELECTOR = '.fable-accordion details, .fable-faq details';
    const CONTENT = '.accordion__content, .fable-faq__answer';
    const DURATION = 300;
    const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
    const running = new WeakMap();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    // a cancelled animation reports back asynchronously: only the current one may clean up
    const finish = (details, content, animation) => {
      if (running.get(details) !== animation) return;
      running.delete(details);
      details.classList.remove('is-closing');
      content.style.overflow = '';
    };

    const frames = (content) => {
      const style = window.getComputedStyle(content);
      const paddingTop = parseFloat(style.paddingTop) || 0;
      const paddingBottom = parseFloat(style.paddingBottom) || 0;
      // the theme is border-box throughout, but do not rely on it
      const height = style.boxSizing === 'border-box' ? content.offsetHeight : Math.max(content.clientHeight - paddingTop - paddingBottom, 0);
      return {
        closed: { height: '0px', paddingTop: '0px', paddingBottom: '0px' },
        open: { height: `${height}px`, paddingTop: `${paddingTop}px`, paddingBottom: `${paddingBottom}px` },
      };
    };

    const toggle = (details) => {
      const content = details.querySelector(CONTENT);
      const animation = running.get(details);
      if (animation) animation.cancel();

      if (!content || reduceMotion.matches || typeof content.animate !== 'function') {
        details.classList.remove('is-closing');
        details.open = !details.open;
        return;
      }

      const opening = !details.open || details.classList.contains('is-closing');
      const summary = details.querySelector(':scope > summary');
      // Dawn mirrors the state on summaries it manages; keep it right when a close is reversed
      if (summary && summary.hasAttribute('aria-expanded')) summary.setAttribute('aria-expanded', String(opening));
      details.classList.remove('is-closing');
      details.open = true;
      const { closed, open } = frames(content);
      content.style.overflow = 'hidden';

      const next = content.animate(opening ? [closed, open] : [open, closed], { duration: DURATION, easing: EASING });
      running.set(details, next);
      if (!opening) details.classList.add('is-closing');
      next.onfinish = () => {
        if (running.get(details) !== next) return;
        if (!opening) details.open = false;
        finish(details, content, next);
      };
      next.oncancel = () => finish(details, content, next);
    };

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const summary = event.target.closest('summary');
      if (!summary) return;
      const details = summary.parentElement;
      if (!details || !details.matches(SELECTOR)) return;
      if (event.target.closest('a[href]')) return;
      event.preventDefault();
      toggle(details);
    });

    window.FableAccordion = { toggle, selector: SELECTOR };
  }

  // Theme editor: show the block that was selected in the sidebar
  if (!window.FablePdpEditor) {
    window.FablePdpEditor = true;
    document.addEventListener('shopify:block:select', (event) => {
      const block = event.target;
      if (!(block instanceof Element)) return;
      if (block.matches('.fable-pets__item')) {
        block.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'start' });
      }
      const details = block.matches('.fable-faq__item') ? block : block.matches('.fable-accordion') ? block.querySelector('details') : null;
      if (details && !details.open) {
        details.open = true;
        const summary = details.querySelector(':scope > summary');
        if (summary && summary.hasAttribute('aria-expanded')) summary.setAttribute('aria-expanded', 'true');
      }
    });
  }

  if (!window.FableFeatureGrid) {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    // the visitor's own choice per video ('paused' | 'playing'); it wins over the defaults below
    const choices = new WeakMap();

    const toggleOf = (video) => {
      const tile = video.closest('.fable-feature-grid__tile');
      return tile ? tile.querySelector('[data-video-toggle]') : null;
    };

    // paused by default for reduced motion and data saver, playing otherwise
    const wantsPause = (video) => {
      const choice = choices.get(video);
      if (choice) return choice === 'paused';
      const connection = navigator.connection;
      return reduceMotion.matches || Boolean(connection && connection.saveData);
    };

    const updateToggle = (video) => {
      const toggle = toggleOf(video);
      if (!toggle) return;
      const paused = wantsPause(video);
      const label = paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause;
      toggle.hidden = false;
      toggle.dataset.state = paused ? 'paused' : 'playing';
      if (label) toggle.setAttribute('aria-label', label);
    };

    const sync = (video) => {
      updateToggle(video);
      if (wantsPause(video)) {
        video.removeAttribute('autoplay');
        video.pause();
        return;
      }
      if (video.paused) {
        const attempt = video.play();
        if (attempt && typeof attempt.catch === 'function') {
          attempt.catch((error) => {
            // autoplay refused (for example low power mode): show "play" so one tap starts it
            if (error && error.name === 'NotAllowedError' && choices.get(video) !== 'paused') {
              choices.set(video, 'paused');
              updateToggle(video);
            }
          });
        }
      }
    };

    const init = (root) => {
      (root || document).querySelectorAll('.fable-feature-grid video').forEach(sync);
    };

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const toggle = event.target.closest('.fable-feature-grid [data-video-toggle]');
      if (!toggle) return;
      const tile = toggle.closest('.fable-feature-grid__tile');
      const video = tile ? tile.querySelector('video') : null;
      if (!video) return;
      choices.set(video, wantsPause(video) ? 'playing' : 'paused');
      sync(video);
    });

    const ready = () => init(document);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', ready);
    } else {
      ready();
    }
    document.addEventListener('shopify:section:load', (event) => init(event.target));
    if (typeof reduceMotion.addEventListener === 'function') reduceMotion.addEventListener('change', ready);

    window.FableFeatureGrid = { init };
  }
})();
